import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from "axios";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API_URL from '../config';
import styled from 'styled-components';

const sections = [
  { id: 1, key: "ss",      validate: "ss",      displayName: "Субботняя школа" },
  { id: 2, key: "mr",      validate: "mr",      displayName: "Утренние чтения" },
  { id: 3, key: "er",      validate: "er",      displayName: "Вечерние чтения" },
  { id: 4, key: "ss-week", validate: "ss-week", displayName: "СШ (Week)" },
];

// ─── Styled ───────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
  margin-bottom: 1.5rem;
`;

const SectionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
`;

const SectionTab = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1.1rem;
  border-radius: 10px;
  border: 1.5px solid ${p => p.isActive ? '#4f46e5' : '#e2e8f0'};
  background: ${p => p.isActive ? '#4f46e5' : '#f8fafc'};
  color: ${p => p.isActive ? '#fff' : '#475569'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  min-width: 130px;

  &:hover {
    border-color: #4f46e5;
    background: ${p => p.isActive ? '#4338ca' : '#eef2ff'};
    color: ${p => p.isActive ? '#fff' : '#4f46e5'};
  }
`;

const TabName = styled.span`
  font-size: 13.5px;
  font-weight: ${p => p.isActive ? 700 : 500};
  line-height: 1.3;
`;

const TabDate = styled.span`
  font-size: 11px;
  margin-top: 4px;
  opacity: ${p => p.isActive ? 0.8 : 0.55};
  font-weight: 400;
`;

const DropArea = styled.div`
  border: 2px dashed ${p => p.isDragActive ? '#4f46e5' : '#cbd5e1'};
  border-radius: 12px;
  background: ${p => p.isDragActive ? '#eef2ff' : '#f8fafc'};
  padding: 2.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 1rem;

  &:hover {
    border-color: #4f46e5;
    background: #eef2ff;
  }
`;

const DropIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #e0e7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.25rem;

  svg { color: #4f46e5; }
`;

const DropText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  text-align: center;

  strong { color: #4f46e5; }
`;

const DropHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 1.25rem;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  background: #f8fafc;
  border-left: 3px solid ${p => p.isValid ? '#4f46e5' : '#ef4444'};
`;

const FileName = styled.span`
  font-size: 13px;
  color: #334155;
  word-break: break-all;
  flex: 1;
`;

const FileSize = styled.span`
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
`;

const RemoveBtn = styled.button`
  padding: 0.25rem 0.625rem;
  border: none;
  border-radius: 6px;
  background: #fee2e2;
  color: #ef4444;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover { background: #fecaca; }
`;

const ErrorMsg = styled.p`
  font-size: 13px;
  color: #ef4444;
  margin: 0.5rem 0 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const UploadBtn = styled.button`
  width: 100%;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 9px;
  background: ${p => p.disabled ? '#e2e8f0' : '#4f46e5'};
  color: ${p => p.disabled ? '#94a3b8' : '#fff'};
  font-size: 14.5px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #4338ca;
    transform: translateY(-1px);
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

function FileUpload({ theme, savedPassword }) {
  const [dates, setDates] = useState({ ss: '', mr: '', er: '' });
  const [files, setFiles] = useState([]);
  const [indexSection, setIndexSection] = useState(0);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const getDates = async () => {
    for (const section of sections) {
      try {
        const response = await axios.get(`${API_URL}/api/audio/last-audio/${section.key}`);
        if (!response) return;
        if (+response.status >= 300 || +response.status < 200) return;
        if (response.data) {
          setDates(prev => ({ ...prev, [section.key]: response.data }));
        }
      } catch (e) {
        console.log('e', e);
      }
    }
  };

  useEffect(() => { getDates(); }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleRemoveFile = (fileName) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
  };

  const handleUploadFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/audio/check-access`, {
        params: { password: savedPassword, section: sections[indexSection].key },
      });
      if (!response.data.isValid) { setError(response.data.message); return; }
    } catch {
      setError("Проверьте соединение с интернетом");
      return;
    }

    const formData = new FormData();
    const filtered = files.filter(f => validateFileName(sections[indexSection].validate, f.name));
    filtered.forEach(f => formData.append('audio', f));

    if (filtered.length === 0) { setError(''); return; }

    setIsUploading(true);
    fetch(`${API_URL}/api/audio/upload/${sections[indexSection].key}`, {
      method: 'POST',
      headers: { 'Accept': '*/*' },
      body: formData,
    })
      .then(() => {
        toast.success('Файлы успешно загружены!');
        setFiles([]);
        setError('');
        getDates();
      })
      .catch(() => toast.error('Ошибка загрузки файлов'))
      .finally(() => setIsUploading(false));
  };

  const getPatternByType = (type) => {
    switch (type) {
      case 'ss':      return /SS\d{4}_\d{2}_\d{2}\.mp3/;
      case 'mr':      return /MR\d{4}_\d{2}_\d{2}\.mp3/;
      case 'er':      return /ER\d{4}_\d{2}_\d{2}\.mp3/;
      case 'ss-week': return /ss_\d{4}_\d{2}_\d{2}\.mp3/;
      default:        return /SS\d{4}_\d{2}_\d{2}\.mp3/;
    }
  };

  const validateFileName = (type, name) => getPatternByType(type).test(name);

  const canUpload = files.length > 0 && !isUploading;

  return (
    <Card>
      <SectionRow>
        {sections.map((item, index) => (
          <SectionTab
            key={item.id}
            isActive={indexSection === index}
            onClick={() => setIndexSection(index)}
          >
            <TabName isActive={indexSection === index}>{item.displayName}</TabName>
            {dates[item.key] && (
              <TabDate isActive={indexSection === index}>до {dates[item.key]}</TabDate>
            )}
          </SectionTab>
        ))}
      </SectionRow>

      <DropArea {...getRootProps()} isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <DropIcon>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </DropIcon>
        <DropText>
          {isDragActive ? 'Отпустите файлы…' : <><strong>Выберите файлы</strong> или перетащите сюда</>}
        </DropText>
        <DropHint>MP3, формат: SS/MR/ER_YYYY_MM_DD.mp3</DropHint>
      </DropArea>

      {files.length > 0 && (
        <FileList>
          {files.map(file => {
            const isValid = validateFileName(sections[indexSection].validate, file.name);
            return (
              <FileRow key={file.name} isValid={isValid}>
                <FileName>{file.name}</FileName>
                <FileSize>{(file.size / 1024 / 1024).toFixed(2)} MB</FileSize>
                <RemoveBtn onClick={() => handleRemoveFile(file.name)}>Удалить</RemoveBtn>
              </FileRow>
            );
          })}
        </FileList>
      )}

      {error && <ErrorMsg>⚠ {error}</ErrorMsg>}

      <UploadBtn onClick={handleUploadFiles} disabled={!canUpload}>
        {isUploading ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            Загрузка…
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Загрузить файлы
          </>
        )}
      </UploadBtn>

      <ToastContainer position="bottom-right" />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Card>
  );
}

export default FileUpload;
